import React, { useState } from 'react';
import {
  ArrowRight,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Fuel,
  HeadphonesIcon,
  Layers,
  Play,
  Settings,
  Shield,
  Truck,
  Wallet,
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';

const Pricing: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const plans = [
    {
      name: 'Starter',
      product: 'Fleet Essentials',
      price: '399',
      unit: '/ vehicle / mo',
      audience: 'Small operators, owner-drivers',
      fleetSize: '1-25 vehicles',
      description:
        'A lean entry point for operators who need trip control, document visibility, and core fleet workflows without upfront complexity.',
      features: [
        'Driver and vendor onboarding',
        'Vehicle and document management',
        'Booking and trip management',
        'Expiry reminders for RC, PUC, and insurance',
        'KYC manual approval workflow',
        'MIS and operational reports',
      ],
    },
    {
      name: 'Growth',
      product: 'Fleet Operations',
      price: '799',
      unit: '/ vehicle / mo',
      audience: 'Mid-size 3PLs and transporters',
      fleetSize: 'Up to 100 vehicles',
      description:
        'The best fit for operators who need faster billing, cleaner branch-level coordination, and stronger day-to-day operational control.',
      features: [
        'Everything in Fleet Essentials',
        'Digital verify and approval workflow',
        'Auto invoice generation',
        'Inward and outward payments',
        'Dynamic pricing rules engine',
        'Multi-branch support',
      ],
      popular: true,
    },
    {
      name: 'Fleet Pro',
      product: 'Fleet Pro',
      price: '1,199',
      unit: '/ vehicle / mo',
      audience: 'Multi-depot fleets',
      fleetSize: 'Up to 500 vehicles',
      description:
        'Built for larger freight businesses managing maintenance, cost control, inventory, and operational performance at scale.',
      features: [
        'Everything in Fleet Operations',
        'Vehicle maintenance and repairs',
        'SPI spare parts inventory',
        'Fuel and lubricant management',
        'Petty cash and expenditure control',
        'Advanced analytics dashboard',
      ],
    },
    {
      name: 'Enterprise',
      product: 'Fleet Enterprise',
      price: 'Custom',
      unit: 'pricing',
      audience: 'Large enterprises and government',
      fleetSize: '500+ vehicles',
      description:
        'A custom rollout for complex operations that need integrations, white-label workflows, dedicated support, and enterprise-grade reliability.',
      features: [
        'Everything in Fleet Pro',
        'Dedicated account manager',
        'White-label option',
        'API integrations with ERP and HRMS',
        'SLA-backed uptime guarantee',
        '24/7 support',
      ],
    },
  ];

  const addOns = [
    {
      name: 'Maintenance Pack',
      price: '199',
      unit: '/ vehicle / mo',
      icon: Settings,
      description:
        'Adds a full maintenance module for fleets that need stronger service control and breakdown workflows.',
      highlights: [
        'Breakdown ticket management',
        'SPI spare parts inventory',
        'Mechanic assignment and job cards',
        'Preventive maintenance reminders',
      ],
      bestFor: 'Best for Fleet Pro upgrade or any base plan that needs maintenance depth',
    },
    {
      name: 'Billing & Finance Pack',
      price: '149',
      unit: '/ vehicle / mo',
      icon: Wallet,
      description:
        'Adds deeper finance workflows for businesses that need invoice control, vendor payment handling, and reconciliation support.',
      highlights: [
        'Auto invoicing and invoice approval workflow',
        'Vendor payables and outward payments',
        'Account receivables tracking',
        'Full payment reconciliation',
      ],
      bestFor: 'Best for operations teams that need a stronger finance layer',
    },
    {
      name: 'Fuel & Petty Cash Pack',
      price: '99',
      unit: '/ vehicle / mo',
      icon: Fuel,
      description:
        'Adds day-to-day spend control for fleets with heavy route operations and frequent driver expense handling.',
      highlights: [
        'Fuel tracking with odometer readings',
        'Auto fuel efficiency calculation',
        'Misuse alerts',
        'Petty cash audit trail',
      ],
      bestFor: 'Best for depot operations and high-mileage fleets',
    },
  ];

  const comparisonRows = [
    { label: 'Trip and booking management', values: ['Yes', 'Yes', 'Yes', 'Yes'] },
    { label: 'Vehicle and document management', values: ['Yes', 'Yes', 'Yes', 'Yes'] },
    { label: 'Dynamic pricing rules', values: ['No', 'Yes', 'Yes', 'Yes'] },
    { label: 'Auto invoice generation', values: ['No', 'Yes', 'Yes', 'Yes'] },
    { label: 'Multi-branch support', values: ['No', 'Yes', 'Yes', 'Yes'] },
    { label: 'Maintenance workflows', values: ['Add-on', 'Add-on', 'Yes', 'Yes'] },
    { label: 'Advanced analytics', values: ['Basic', 'Basic', 'Advanced', 'Advanced'] },
    { label: 'API integrations', values: ['No', 'No', 'Optional', 'Yes'] },
    { label: 'Dedicated account manager', values: ['No', 'No', 'Optional', 'Yes'] },
  ];

  const faqs = [
    {
      q: 'Is pricing charged per vehicle?',
      a: 'Yes. The core plans are structured around a per-vehicle monthly model, which matches how Indian fleet SaaS buyers evaluate software value and scale.',
    },
    {
      q: 'Can we start with a base plan and add modules later?',
      a: 'Yes. The pricing model is designed to let customers start lean and expand with add-on packs such as maintenance, billing and finance, or fuel and petty cash controls.',
    },
    {
      q: 'Which plan is best for a growing transport company?',
      a: 'Growth is the strongest default for mid-size operators because it adds dynamic pricing, auto invoicing, and multi-branch support while keeping the rollout straightforward.',
    },
    {
      q: 'When should we choose Fleet Pro or Enterprise?',
      a: 'Fleet Pro fits multi-depot fleets that need deeper maintenance, expense, and analytics workflows. Enterprise is best for complex rollouts requiring integrations, white-label support, and SLA-backed service.',
    },
    {
      q: 'Are onboarding and support included?',
      a: 'The page positions onboarding and support as part of the commercial value story, with Enterprise including the deepest support model and dedicated account management.',
    },
    {
      q: 'Can we get a tailored quote instead of choosing a public plan?',
      a: 'Yes. Enterprise and larger custom rollouts can be handled through a tailored quote, especially when the operation spans multiple branches, product lines, or integration needs.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <AnimatedBackground />

      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-accent-orange/5" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6">
              <CircleDollarSign className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">Flexible Plans for Fleets of All Sizes</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-950 leading-tight mb-6">
              Pricing Built for <span className="text-primary-600">How Fleets Actually Scale</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10">
              A pay-per-vehicle SaaS model with modular add-ons, so operators can start lean, add control where they
              need it, and expand without changing platforms.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a href="#plans" className="btn-primary">
                See Plans
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <a href="#contact" className="btn-secondary">
                <Play className="w-5 h-5 mr-2" />
                Request Demo
              </a>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="text-3xl font-bold text-navy-950 mb-1">₹399</div>
                <div className="text-sm text-gray-500 mb-2">starting price / vehicle / mo</div>
                <div className="text-sm font-medium text-primary-600">Lean entry point for small operators</div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="text-3xl font-bold text-navy-950 mb-1">Modular</div>
                <div className="text-sm text-gray-500 mb-2">add-on pricing model</div>
                <div className="text-sm font-medium text-primary-600">Expand with maintenance, finance, and fuel controls</div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="text-3xl font-bold text-navy-950 mb-1">500+</div>
                <div className="text-sm text-gray-500 mb-2">vehicle enterprise rollouts</div>
                <div className="text-sm font-medium text-primary-600">Custom commercial models available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-navy-950">
        <div className="container-main">
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: 'Pay Per Vehicle', desc: 'Matches how Indian fleet software is actually bought and scaled.' },
              { title: 'Start Lean', desc: 'Operators can begin with a practical base plan instead of overbuying.' },
              { title: 'Add Modules Later', desc: 'Maintenance, finance, and fuel controls layer in as operations mature.' },
              { title: 'Enterprise Ready', desc: 'Custom rollout models support large fleets, government, and integrations.' },
            ].map((item, index) => (
              <div key={index} className="bg-white/5 rounded-2xl border border-white/10 p-6">
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="plans" className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">Choose the Right Base Plan</h2>
            <p className="text-lg text-gray-600">
              The pricing architecture is designed to reduce buying friction: clear entry tiers, stronger mid-market value,
              and a custom enterprise path when the operation becomes more complex.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`card p-6 relative h-full flex flex-col ${plan.popular ? 'border-2 border-primary-500 shadow-lg shadow-primary-500/10' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span>
                  </div>
                )}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-primary-600 mb-2">{plan.name}</p>
                  <h3 className="text-2xl font-bold text-navy-950 mb-2">{plan.product}</h3>
                  <div className="flex items-end gap-1 mb-2">
                    {plan.price === 'Custom' ? (
                      <span className="text-4xl font-bold text-navy-950">Custom</span>
                    ) : (
                      <>
                        <span className="text-lg font-semibold text-gray-500">₹</span>
                        <span className="text-4xl font-bold text-navy-950">{plan.price}</span>
                      </>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mb-3">{plan.unit}</div>
                  <div className="text-sm font-medium text-navy-950">{plan.audience}</div>
                  <div className="text-xs text-primary-600 mt-1">{plan.fleetSize}</div>
                </div>

                <p className="text-sm text-gray-600 mb-6">{plan.description}</p>

                <ul className="space-y-3 mb-6 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a href="#contact" className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'} text-center`}>
                  Request Demo
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">Usage-Based Add-On Packs</h2>
            <p className="text-lg text-gray-600">
              The modular layer is what makes this pricing UX strong: teams can buy the control they need without forcing a heavy enterprise commitment too early.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {addOns.map((addon, index) => (
              <div key={index} className="card p-6 h-full">
                <div className="w-14 h-14 bg-primary-50 rounded-2xl flex items-center justify-center mb-5">
                  <addon.icon className="w-7 h-7 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950 mb-2">{addon.name}</h3>
                <div className="flex items-end gap-1 mb-3">
                  <span className="text-lg font-semibold text-gray-500">₹</span>
                  <span className="text-3xl font-bold text-navy-950">{addon.price}</span>
                </div>
                <div className="text-sm text-primary-600 font-medium mb-4">{addon.unit}</div>
                <p className="text-sm text-gray-600 mb-5">{addon.description}</p>
                <ul className="space-y-3 mb-5">
                  {addon.highlights.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-500 border-t border-gray-100 pt-4">{addon.bestFor}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6">
                <Layers className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-700">Why This Commercial Model Fits Fleet Operations</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-6">
                Built for How Fleet Businesses Actually Buy and Grow
              </h2>
              <div className="space-y-4">
                {[
                  'Public starting prices make GaadiMatic easier to evaluate for operators comparing vendors.',
                  'Per-vehicle pricing maps cleanly to how fleet owners estimate software ROI and operating scale.',
                  'A strong middle tier creates a practical default for growing transport businesses and 3PLs.',
                  'Modular add-ons let operations teams expand by workflow instead of overbuying on day one.',
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-navy-950 rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold text-white mb-6">Fleet Pricing Principles</h3>
              <div className="space-y-4">
                {[
                  { icon: Truck, title: 'Fleet-Aligned', desc: 'Scales with the number of active vehicles, not abstract seats or users.' },
                  { icon: Wallet, title: 'Expansion-Friendly', desc: 'Add maintenance, finance, or fuel workflows only when they become operational priorities.' },
                  { icon: Shield, title: 'Enterprise-Safe', desc: 'Custom rollouts remain available for government, large fleets, and integration-heavy accounts.' },
                  { icon: HeadphonesIcon, title: 'Sales-Ready', desc: 'Clear pricing improves engagement while preserving room for custom enterprise selling.' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">Compare Plans at a Glance</h2>
            <p className="text-lg text-gray-600">
              A comparison table adds confidence on pricing pages because buyers can see the upgrade logic without asking sales first.
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 font-semibold text-navy-950">Capability</th>
                  {plans.map((plan) => (
                    <th key={plan.name} className="px-6 py-4 text-left font-semibold text-navy-950">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, index) => (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="px-6 py-4 text-sm font-medium text-gray-700">{row.label}</td>
                    {row.values.map((value, i) => (
                      <td key={i} className="px-6 py-4 text-sm text-gray-600">
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">
              Pricing pages convert better when they answer buying questions directly instead of pushing every decision into a demo call.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-navy-950">{faq.q}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section-padding bg-navy-950">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Want a Pricing Walkthrough for Your Fleet?
            </h2>
            <p className="text-lg text-gray-400 mb-10">
              Tell us your fleet size, operating model, and modules you care about. We’ll help you choose the right plan,
              add-ons, and rollout path.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="btn-primary text-lg px-10">
                Request Demo
              </a>
              <a
                href="tel:+919916500454"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border-2 border-white/30 rounded-lg transition-all duration-200 hover:bg-white/10"
              >
                Talk to Sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
